'use client';

import { useState, useEffect, useRef } from 'react';
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { Partners } from '@/components/partners';
import { HeroSection } from '@/components/careers/HeroSection';
import { CtaSection } from '@/components/careers/CtaSection';
import { JobModal } from '@/components/careers/JobModal';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from "react-i18next";

import type { Job } from '@/types/job';
import type { ApplicationForm } from '@/types/application';


export default function CareersPage() {
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState<ApplicationForm>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    linkedin: '',
    coverLetter: '',
    jobId: '',
    agreeTerms: false,
    resumeFile: null,
    resumeFileName: '',
  });

  const resetModal = () => {
    setIsApplying(false);
    setApplied(false);
    setDuplicate(false);
    setSelectedJob(null);
  };



  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

  /* ------------------------------------------------------------------ */
  /*  FETCH JOBS                                                       */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (isModalOpen) fetchJobs();
  }, [isModalOpen]);

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      const { data } = await axios.get<{ jobs: Job[] }>(`${API_BASE_URL}/jobs`);
      setJobs(data.jobs ?? []);
    } catch {
      setError('Failed to load jobs.');
      toast({ title: 'Error', description: 'Could not load jobs.', variant: 'destructive' });
    } finally {
      setLoadingJobs(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  MODAL HANDLERS                                                    */
  /* ------------------------------------------------------------------ */
  const handleApply = (job: Job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, jobId: job.id }));
    setIsApplying(true);
    setDuplicate(false);  
    setApplied(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = new FormData();

    Object.entries(formData).forEach(([k, v]) => {
      if (k === "resumeFile" && v) data.append("resume", v);
      else if (k !== "resumeFile" && k !== "resumeFileName") data.append(k, String(v));
    });

    try {
      await axios.post(`${API_BASE_URL}/job-application`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ── SUCCESS ─────────────────────────────────────
      setApplied(true);
      toast({ title: "Success!", description: "Application sent." });
    } catch (err: any) {
      // ── 409 → already applied ───────────────────────
      if (err.response?.status === 409) {
        setDuplicate(true);               // ← show duplicate panel
        toast({
          title: "Already applied",
          description: "You have already submitted an application for this position.",
          variant: "default",
        });
      } else {
        // ── any other error ───────────────────────────
        toast({
          title: "Error",
          description: "Submission failed.",
          variant: "destructive",
        });
      }
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.type === 'application/pdf' && file.size <= 5 * 1024 * 1024) {
      setFormData(prev => ({ ...prev, resumeFile: file, resumeFileName: file.name }));
    } else {
      toast({ title: 'Invalid', description: 'PDF only, max 5 MB.', variant: 'destructive' });
    }
  };

  const removeFile = () => {
    setFormData(prev => ({ ...prev, resumeFile: null, resumeFileName: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />
      <main className="flex-grow">
        <HeroSection onOpenModal={() => setIsModalOpen(true)} />


        {/* About Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 mb-8">
                {t('careers.about', "At Oleyes Security, we believe the future of safety lies at the intersection of AI, cloud technology, and cybersecurity. We're not just building tools—we're shaping how businesses, schools, banks, and entire communities protect their people and assets. When you join Oleyes, you're not just taking a job—you're taking part in a mission to redefine security for the modern world.")}
              </p>

              <div className="mt-12">
                <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">
                  {t('careers.aboutTitle', 'About Oleyes Security')}
                </h2>
                <p className="text-gray-700 dark:text-slate-300 mb-6">
                  {t('careers.aboutDescription', 'Oleyes Security is an AI-powered video surveillance and analytics platform that brings advanced detection and cloud connectivity to organizations of all sizes.')}
                </p>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-md bg-slate-800 text-white dark:bg-slate-700">
                        <span className="text-lg">✓</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                        {t('careers.feature1.title', 'Real-Time Threat Detection')}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-300">
                        {t('careers.feature1.description', 'Fire, smoke, violence, theft, and falls detected by AI with instant alerts.')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-md bg-slate-800 text-white dark:bg-slate-700">
                        <span className="text-lg">✓</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                        {t('careers.feature2.title', 'Plug & Play Deployment')}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-300">
                        {t('careers.feature2.description', 'Devices connect seamlessly to the cloud through our Oleyes Security Gateway.')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-md bg-slate-800 text-white dark:bg-slate-700">
                        <span className="text-lg">✓</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                        {t('careers.feature3.title', 'AR Visual Audits')}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-300">
                        {t('careers.feature3.description', 'Innovative tools to help businesses optimize their spaces and eliminate blind spots.')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-md bg-slate-800 text-white dark:bg-slate-700">
                        <span className="text-lg">✓</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                        {t('careers.feature4.title', 'Pure Cloud Management')}
                      </h3>
                      <p className="text-slate-600 dark:text-slate-300">
                        {t('careers.feature4.description', 'Centralized, cybersecure, and scalable video monitoring from anywhere in the world.')}
                      </p>
                    </div>
                  </div>
                </div>

                <p className="mt-8 text-gray-700 dark:text-slate-300">
                  {t('careers.aboutConclusion', "We're trusted by retailers, schools, banks, hospitals, and enterprises looking to combine safety with efficiency. Our vision is clear: to become the global standard for AI-driven video intelligence.")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Work With Us Section */}
        <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-800/30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('careers.whyWorkWithUs', 'Why Work With Us?')}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800/50 flex-shrink-0 flex items-center justify-center">
                    <svg className="h-6 w-6 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                    {t('careers.benefit1.title', 'Mission That Matters')}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.benefit1.description', 'Every line of code, every deployment, directly contributes to preventing incidents, protecting lives, and creating safer environments.')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800/50 flex-shrink-0 flex items-center justify-center">
                    <svg className="h-6 w-6 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                    {t('careers.benefit2.title', 'Innovation Culture')}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.benefit2.description', 'We\'re a team of bold thinkers, problem-solvers, and builders who thrive on innovation and experimentation.')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800/50 flex-shrink-0 flex items-center justify-center">
                    <svg className="h-6 w-6 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                    {t('careers.benefit3.title', 'Global Impact')}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.benefit3.description', 'From Tunisia to the world, our solutions serve international clients and partners.')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800/50 flex-shrink-0 flex items-center justify-center">
                    <svg className="h-6 w-6 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                    {t('careers.benefit4.title', 'Growth-Focused')}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.benefit4.description', 'We invest in our people with training, mentorship, and opportunities to grow your career alongside the company\'s success.')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800/50 flex-shrink-0 flex items-center justify-center">
                    <svg className="h-6 w-6 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white">
                    {t('careers.benefit5.title', 'Collaborative Team')}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.benefit5.description', 'Small enough to be agile, big enough to make an impact. You\'ll work closely with engineers, researchers, and business leaders.')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Life at Oleyes Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('careers.lifeAtOleyes.title', 'Life at Oleyes Security')}
              </h2>
              <p className="mt-4 text-lg text-gray-600 dark:text-slate-300 max-w-3xl mx-auto">
                {t('careers.lifeAtOleyes.subtitle', 'We believe the best ideas come from an environment that values creativity, inclusivity, and balance.')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('careers.lifeAtOleyes.feature1', 'Flexible Work')}
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  {t('careers.lifeAtOleyes.feature1Desc', 'Work from our offices or remotely, with flexible hours to suit your lifestyle.')}
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('careers.lifeAtOleyes.feature2', 'Diverse Culture')}
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  {t('careers.lifeAtOleyes.feature2Desc', 'A culture that celebrates diversity and values every team member\'s unique perspective.')}
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('careers.lifeAtOleyes.feature3', 'Cutting-Edge Tech')}
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  {t('careers.lifeAtOleyes.feature3Desc', 'Access to the latest AI, cloud, and computer vision tools to build the future of security.')}
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 dark:bg-slate-800/50 flex items-center justify-center mb-4">
                  <svg className="h-8 w-8 text-slate-800 dark:text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('careers.lifeAtOleyes.feature4', 'Your Voice Matters')}
                </h3>
                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  {t('careers.lifeAtOleyes.feature4Desc', 'A supportive environment where your ideas are heard and valued.')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What We Look For Section */}
        <section className="py-16 md:py-24 bg-slate-50 dark:bg-slate-800/30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('careers.whatWeLookFor.title', 'What We Look For')}
              </h2>
              <p className="mt-4 text-lg text-gray-600 dark:text-slate-300 max-w-3xl mx-auto">
                {t('careers.whatWeLookFor.subtitle', 'We\'re looking for passionate people who are:')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-3">
                  {t('careers.whatWeLookFor.trait1', 'Driven by purpose')}
                </h3>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.whatWeLookFor.trait1Desc', 'You care about creating safer communities.')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-3">
                  {t('careers.whatWeLookFor.trait2', 'Curious and innovative')}
                </h3>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.whatWeLookFor.trait2Desc', 'You challenge norms and push boundaries.')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-3">
                  {t('careers.whatWeLookFor.trait3', 'Collaborative')}
                </h3>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.whatWeLookFor.trait3Desc', 'You thrive in team-based environments.')}
                </p>
              </div>

              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-3">
                  {t('careers.whatWeLookFor.trait4', 'Resilient and adaptable')}
                </h3>
                <p className="text-gray-600 dark:text-slate-300">
                  {t('careers.whatWeLookFor.trait4Desc', 'You embrace change and fast growth.')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Hiring Process Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('careers.hiringProcess.title', 'Our Hiring Process')}
              </h2>
              <p className="mt-4 text-lg text-gray-600 dark:text-slate-300 max-w-3xl mx-auto">
                {t('careers.hiringProcess.subtitle', 'We\'ve designed our hiring process to be straightforward and transparent.')}
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-slate-800 text-white dark:bg-slate-700 flex items-center justify-center text-2xl font-bold mb-4">
                    {step}
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {t(`careers.hiringProcess.step${step}.title`,
                      step === 1 ? 'Apply Online' :
                        step === 2 ? 'First Interview' :
                          step === 3 ? 'Skills Assessment' :
                            step === 4 ? 'Team Interview' : 'Offer & Onboarding'
                    )}
                  </h3>
                  <p className="mt-2 text-slate-600 dark:text-slate-300 text-sm">
                    {t(`careers.hiringProcess.step${step}.description`,
                      step === 1 ? 'Submit your application and resume.' :
                        step === 2 ? 'A casual chat to get to know each other.' :
                          step === 3 ? 'Show us your skills with a practical test.' :
                            step === 4 ? 'Meet the team and see the culture.' : 'Welcome to Oleyes Security!'
                    )}
                  </p>
                </div>
              ))}
            </div>


          </div>
        </section>
        <CtaSection onOpenModal={() => setIsModalOpen(true)} />
      </main>
      <Partners />
      <Footer />

      <JobModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        jobs={jobs}
        loading={loadingJobs}
        error={error}
        selectedJob={selectedJob}
        isApplying={isApplying}
        applied={applied}
        duplicate={duplicate}
        formData={formData}
        setFormData={setFormData}
        onApply={handleApply}
        onBack={() => setIsApplying(false)}
        onSubmit={handleSubmit}
        onFileDrop={handleFileDrop}
        onFileSelect={(f) => setFormData(prev => ({ ...prev, resumeFile: f, resumeFileName: f?.name || '' }))}
        removeFile={removeFile}
        resetModal={resetModal}
        fileInputRef={fileInputRef}
        fetchJobs={fetchJobs}
      />
    </div>
  );
}


